import { Drawer, Layout, notification } from 'antd'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { ResellerLicenseLimitWarning, Theme } from '../types'
import Header from './Header'
import styles from './index.module.less'
import SystemMenu from './SystemMenu'
import GlobalSearch from './GlobalSearch'
import { useBreakpoints } from '../components'
import Footer from './Footer'
import './menu.less'
import { resellerService } from '../services'
import { useAuth } from '../providers/AuthProvider'
import config from '../config'

const { Sider, Content } = Layout

interface DesktopMenuProps {
	collapsed: boolean
	theme: Theme
}

interface MobileMenuProps extends DesktopMenuProps {
	onClose(): void
	onSelect(): void
}

interface DashboardProps {
	theme: Theme
	children: JSX.Element
}

const MobileMenu: FC<MobileMenuProps> = ({ theme, onClose, collapsed, onSelect }) => {
	return (
		<Drawer closable={false} maskClosable onClose={onClose} bodyStyle={{ padding: 0 }} placement='left' visible={!collapsed}>
			<SystemMenu onSelect={onSelect} theme={theme} />
			<Footer className={styles.footer} privacyPolicyLink={config.privacyPolicyLink()} versionNumber={config.appVersionNumber()} />
		</Drawer>
	)
}

const DesktopMenu: FC<DesktopMenuProps> = ({ collapsed, theme }) => (
	<Sider className={styles.sider} trigger={null} collapsible width={styles.siderWidth} collapsed={collapsed} collapsedWidth={0}>
		<div className={styles.siderChild}>
			<SystemMenu theme={theme} />
			<Footer className={styles.footer} privacyPolicyLink={config.privacyPolicyLink()} versionNumber={config.appVersionNumber()} />
		</div>
	</Sider>
)

const Dashboard: FC<DashboardProps> = ({ theme, children }) => {
	const [isCollapsed, setIsCollapsed] = useState<boolean>(false)
	const { userIsResellerAdmin, user } = useAuth()
	const breakpoints = useBreakpoints()

	const getLimitWarnings = useCallback(async () => {
		try {
			const requests = user.resellers?.map(({ id }) => resellerService.getLicensesNearLimit(id))
			const results = await Promise.all(requests)
			return results.map(({ data }) => Object.values(data)).flat()
		} catch (error) {
			// silently fail
			return []
		}
	}, [])

	const showLicenseLimitWarnings = (limitWarnings: ResellerLicenseLimitWarning[]) => {
		notification.warn({
			message: 'License limit nearly reached',
			description: (
				<ul className={styles.licenseLimitWarningList}>
					{limitWarnings?.map(limit => (
						<li key={limit.type}>
							{limit.unallocated} {limit.type} licenses remaining
						</li>
					))}
				</ul>
			),
			duration: 0,
			type: 'warning',
			placement: 'topRight'
		})
	}

	useEffect(() => {
		const hasLimitWarningShown = !!window.sessionStorage.getItem('hasLimitWarningShown')
		if (!hasLimitWarningShown && userIsResellerAdmin) {
			getLimitWarnings().then(limitWarnings => {
				if (limitWarnings?.length > 0) {
					showLicenseLimitWarnings(limitWarnings)
					window.sessionStorage.setItem('hasLimitWarningShown', 'true')
				}
			})
		}
	}, [getLimitWarnings])

	return (
		<Layout className={styles.layout}>
			{breakpoints.xs ? (
				<MobileMenu onSelect={(): void => setIsCollapsed(true)} collapsed={isCollapsed} onClose={(): void => setIsCollapsed(true)} theme={theme} />
			) : (
				<DesktopMenu collapsed={isCollapsed} theme={theme} />
			)}
			<Layout className={[styles.wrapper, isCollapsed ? '' : styles.collapsed].join(' ')}>
				<Header collapsed={isCollapsed} setCollapsed={setIsCollapsed}>
					<GlobalSearch />
				</Header>
				<Content className={styles.content}>{children}</Content>
			</Layout>
		</Layout>
	)
}

export default Dashboard
